'use client';

import {
  OTPInput,
  OTPInputContext,
  REGEXP_ONLY_DIGITS,
  type RenderProps,
} from 'input-otp';
import { Dot } from 'lucide-react';
import * as React from 'react';

import { cn } from '../utils/cn';

const stripNonDigits = (pasted: string) => pasted.replace(/\D/g, '');

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(
  (
    {
      className,
      containerClassName,
      inputMode,
      autoComplete,
      pattern,
      pasteTransformer,
      ...props
    },
    ref,
  ) => (
    <OTPInput
      ref={ref}
      containerClassName={cn(
        'flex items-center gap-2 has-[:disabled]:opacity-50',
        containerClassName,
      )}
      className={cn('disabled:cursor-not-allowed', className)}
      inputMode={inputMode ?? 'numeric'}
      autoComplete={autoComplete ?? 'one-time-code'}
      // Digits only by default. The anchored pattern rejects stray characters
      // instead of letting them fill slots ('[0-9]*' matched any string), and
      // pasted codes such as "123 456" are reduced to their digits first.
      pattern={pattern ?? REGEXP_ONLY_DIGITS}
      pasteTransformer={
        pasteTransformer ?? (pattern ? undefined : stripNonDigits)
      }
      {...props}
    />
  ),
);
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center', className)} {...props} />
));
InputOTPGroup.displayName = 'InputOTPGroup';

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext: RenderProps = React.useContext(OTPInputContext);
  const slot = inputOTPContext.slots[index] ?? {
    char: '',
    hasFakeCaret: false,
    isActive: false,
    placeholderChar: null,
  };
  const { char, hasFakeCaret, isActive } = slot;

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center border-y border-r border-border bg-card shadow-xs text-sm font-medium transition-all first:rounded-l-md first:border-l last:rounded-r-md dark:bg-input/30',
        isActive && 'z-10 ring-2 ring-ring ring-offset-background bg-card',
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = 'InputOTPSlot';

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <Dot />
  </div>
));
InputOTPSeparator.displayName = 'InputOTPSeparator';

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
